import React from 'react';

import { Box } from '@chakra-ui/react';
import {
  DISCIPLINARY_ACTIONS_STATUS,
  DISCIPLINARY_ACTIONS_STATUS_COLOR,
  DISCIPLINARY_ACTIONS_STATUS_LABELS,
} from 'Deer/pages/Team/DirectReports/DisciplinaryActions/constants';
import { ICellRendererParams } from 'ag-grid-community';

import { CheckCircle, Delete, Notifications, Publish } from 'assets/assetsV1/iconComponents';

import { Avatar } from 'uiToolkitV1/Avatar';
import { BUTTON_VARIANT, Button } from 'uiToolkitV1/Button';
import { BUTTON_COLORS } from 'uiToolkitV1/Button/Button';
import { TagColored } from 'uiToolkitV1/Tag';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

// Employee data structure from API
interface EmployeeData {
  id: number;
  companyId: number | null;
  displayName: string;
  profilePhoto: string;
  designation: string;
  ownerType: string | null;
  employeeFunction: string | null;
  email: string;
  preferredName: string;
}

// API response type
interface DisciplinaryActionData {
  LOCATION?: string;
  STATUS?: string;
  RAISED_BY?: EmployeeData;
  RAISED_FOR?: EmployeeData;
  DESCRIPTION?: string;
  REPORTED_TIME?: string;
  TYPE?: string;
  COMPANY?: string | null;
}

type ReportedEmployeeProps = {
  screenType?: 'EMPLOYEE' | 'MANAGER';
  status?: (typeof DISCIPLINARY_ACTIONS_STATUS)[keyof typeof DISCIPLINARY_ACTIONS_STATUS];
  onPublish?: () => void;
  onNotify?: () => void;
  onAcknowledge?: () => void;
};

type ConfigEntry = {
  ActionButton: ((props: { onClick?: () => void }) => JSX.Element) | null;
  color: string;
  tagText: string;
};

const EMPLOYEE_CONFIG: Record<string, ConfigEntry> = {
  [DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE]: {
    ActionButton: ({ onClick }: { onClick?: () => void }) => (
      <Button
        variant={BUTTON_VARIANT.tertiary}
        StartIcon={CheckCircle}
        onClick={onClick}
        paddingRight="0px!important"
        sx={{ p: { marginRight: '0px' } }}
      >
        Acknowledge
      </Button>
    ),
    color: DISCIPLINARY_ACTIONS_STATUS_COLOR[DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE],
    tagText: DISCIPLINARY_ACTIONS_STATUS_LABELS.PUBLISHED_TO_EMPLOYEE,
  },
  [DISCIPLINARY_ACTIONS_STATUS.ACKNOWLEDGED]: {
    ActionButton: null,
    color: DISCIPLINARY_ACTIONS_STATUS_COLOR[DISCIPLINARY_ACTIONS_STATUS.ACKNOWLEDGED],
    tagText: DISCIPLINARY_ACTIONS_STATUS_LABELS.ACKNOWLEDGED,
  },
};

const MANAGER_CONFIG: Record<string, ConfigEntry> = {
  [DISCIPLINARY_ACTIONS_STATUS.CREATED]: {
    ActionButton: ({ onClick }: { onClick?: () => void }) => (
      <Button
        variant={BUTTON_VARIANT.tertiary}
        StartIcon={Delete}
        onClick={onClick}
        paddingRight="0px!important"
        sx={{ p: { marginRight: '0px' } }}
        color={BUTTON_COLORS.red}
      >
        Delete
      </Button>
    ),
    color: DISCIPLINARY_ACTIONS_STATUS_COLOR[DISCIPLINARY_ACTIONS_STATUS.CREATED],
    tagText: DISCIPLINARY_ACTIONS_STATUS_LABELS.CREATED,
  },
  [DISCIPLINARY_ACTIONS_STATUS.APPROVED]: {
    ActionButton: ({ onClick }: { onClick?: () => void }) => (
      <Button
        variant={BUTTON_VARIANT.tertiary}
        StartIcon={Publish}
        onClick={onClick}
        paddingRight="0px!important"
        sx={{ p: { marginRight: '0px' } }}
      >
        Publish
      </Button>
    ),
    color: DISCIPLINARY_ACTIONS_STATUS_COLOR[DISCIPLINARY_ACTIONS_STATUS.APPROVED],
    tagText: DISCIPLINARY_ACTIONS_STATUS_LABELS.APPROVED,
  },
  [DISCIPLINARY_ACTIONS_STATUS.DENIED]: {
    ActionButton: null,
    color: DISCIPLINARY_ACTIONS_STATUS_COLOR[DISCIPLINARY_ACTIONS_STATUS.DENIED],
    tagText: DISCIPLINARY_ACTIONS_STATUS_LABELS.DENIED,
  },
  [DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE]: {
    ActionButton: ({ onClick }: { onClick?: () => void }) => (
      <Button
        variant={BUTTON_VARIANT.tertiary}
        StartIcon={Notifications}
        onClick={onClick}
        paddingRight="0px!important"
        sx={{ p: { marginRight: '0px' } }}
      >
        Notify
      </Button>
    ),
    color: DISCIPLINARY_ACTIONS_STATUS_COLOR[DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE],
    tagText: DISCIPLINARY_ACTIONS_STATUS_LABELS.PUBLISHED_TO_EMPLOYEE,
  },
  [DISCIPLINARY_ACTIONS_STATUS.ACKNOWLEDGED]: {
    ActionButton: null,
    color: DISCIPLINARY_ACTIONS_STATUS_COLOR[DISCIPLINARY_ACTIONS_STATUS.ACKNOWLEDGED],
    tagText: DISCIPLINARY_ACTIONS_STATUS_LABELS.ACKNOWLEDGED,
  },
};

function ReportedEmployee({
  screenType = 'EMPLOYEE',
  onPublish,
  onNotify,
  onAcknowledge,
  ...props
}: ICellRendererParams<DisciplinaryActionData> & ReportedEmployeeProps) {
  // Extract data from ag-Grid cell renderer params
  const { data } = props;

  if (!data) return null;
  const isEmployee = screenType === 'EMPLOYEE';

  // Use API response data
  const statusValue = data.STATUS;
  const employeeData = isEmployee ? data.RAISED_BY : data.RAISED_FOR;
  const reportedTime = data.REPORTED_TIME ? new Date(data.REPORTED_TIME) : new Date();

  // Format date and time
  const date = reportedTime.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const time = reportedTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  const config = isEmployee ? EMPLOYEE_CONFIG[statusValue] : MANAGER_CONFIG[statusValue];

  if (!config || !employeeData) {
    return 'Status not applicable ';
  }

  const { color, tagText, ActionButton } = config;

  const getButtonAction = () => {
    if (!ActionButton) return null;

    let buttonAction;
    if (isEmployee) {
      buttonAction = onAcknowledge;
    } else if (statusValue === DISCIPLINARY_ACTIONS_STATUS.APPROVED) {
      buttonAction = onPublish;
    } else {
      buttonAction = onNotify;
    }

    return ActionButton ? <ActionButton onClick={buttonAction} /> : null;
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap="0px"
      alignSelf="stretch"
      padding="12px 0px"
      width="100%"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" alignSelf="stretch">
        <Box display="flex" padding="8px 0px" alignItems="center" gap="8px" flex="1 0 0" borderRadius="0px">
          <Avatar name={employeeData.displayName} src={employeeData.profilePhoto} size={32} />
          <Box display="flex" flexDirection="column">
            <TextBodyMedium bold>{employeeData.displayName}</TextBodyMedium>
            <TextBodyMedium>{employeeData.designation}</TextBodyMedium>
          </Box>
        </Box>
        {getButtonAction()}
      </Box>
      <TextBodyMedium padding="4px" color="#666">
        {date} • {time}
      </TextBodyMedium>
      <Box display="flex" justifyContent="space-between" alignItems="center" alignSelf="stretch">
        <TextBodyMedium maxWidth="110px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {data.TYPE || ''}
        </TextBodyMedium>
        <TagColored color={color}>{tagText}</TagColored>
      </Box>
      <Box padding="8px 0px">
        <TextBodyMedium
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.25em',
            maxHeight: '2.5em',
            wordBreak: 'break-word',
          }}
          maxW="100vw"
        >
          {data.DESCRIPTION || ''}
        </TextBodyMedium>
      </Box>
    </Box>
  );
}

export { ReportedEmployee };
